import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Loader2, ShoppingCart, Trash2, Package, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useWishlist } from "@/context/WishlistContext";
import { useCart } from "@/context/CartContext";
import ProductImage from "@/components/ProductImage";
import AuthModal from "@/components/AuthModal";
import { toast } from "sonner";
import axios from "axios";

var API = "/api";

function formatPrice(p) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(p);
}

export default function WishlistPage() {
  var auth = useAuth();
  var wishlist = useWishlist();
  var cart = useCart();
  var navigate = useNavigate();
  var [items, setItems] = useState([]);
  var [isLoading, setIsLoading] = useState(true);
  var [showAuthModal, setShowAuthModal] = useState(false);

  useEffect(function() {
    if (auth.isLoading) return;
    if (!auth.isAuthenticated) {
      setIsLoading(false);
      setShowAuthModal(true);
      return;
    }
    fetchWishlist();
  }, [auth.isAuthenticated, auth.isLoading]);

  function fetchWishlist() {
    setIsLoading(true);
    axios.get(API + "/wishlist", { withCredentials: true })
      .then(function(res) { setItems(res.data); })
      .catch(function() { toast.error("Failed to load wishlist"); })
      .finally(function() { setIsLoading(false); });
  }

  function handleRemove(productId) {
    wishlist.removeFromWishlist(productId).then(function() {
      setItems(function(prev) { return prev.filter(function(it) { return it.product.id !== productId; }); });
      toast.success("Removed from wishlist");
    }).catch(function() { toast.error("Failed to remove"); });
  }

  function handleAddToCart(product) {
    if (!product.in_stock) {
      toast.info("This item is currently out of stock");
      return;
    }
    cart.addToCart(product, 1);
    toast.success(product.name + " added to cart");
  }

  // Not signed in
  if (!auth.isLoading && !auth.isAuthenticated) {
    return (
      <div className="min-h-screen pt-24 pb-16" data-testid="wishlist-page-signin">
        <div className="container-tactical">
          <div className="max-w-xl mx-auto text-center py-16">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#121212] border border-white/5 flex items-center justify-center">
              <Heart className="w-7 h-7 text-[#C0A062]" />
            </div>
            <h1 className="font-heading font-black text-3xl text-white uppercase mb-3">Sign In to View Your Wishlist</h1>
            <p className="text-[#A1A1AA] mb-8">Save your favorite parts to your account and we'll let you know when out-of-stock items are back.</p>
            <Button className="btn-primary" onClick={function() { setShowAuthModal(true); }} data-testid="wishlist-signin-btn">Sign In</Button>
          </div>
        </div>
        <AuthModal isOpen={showAuthModal} onClose={function() { setShowAuthModal(false); navigate("/"); }} initialMode="login" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="wishlist-page">
      <div className="container-tactical">
        <div className="mb-8">
          <p className="font-mono text-[#C0A062] text-sm uppercase mb-2">Your Collection</p>
          <h1 className="font-heading font-black text-4xl text-white uppercase">Wishlist</h1>
          <p className="text-[#A1A1AA] mt-2">{items.length} {items.length === 1 ? "item" : "items"} saved</p>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-24">
            <Loader2 className="w-8 h-8 text-[#C0A062] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-24" data-testid="wishlist-empty">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#121212] border border-white/5 flex items-center justify-center">
              <Heart className="w-7 h-7 text-[#71717a]" />
            </div>
            <h3 className="font-heading font-bold text-white text-xl uppercase mb-2">Your wishlist is empty</h3>
            <p className="text-[#A1A1AA] mb-6">Save parts you love and check on them anytime.</p>
            <Link to="/shop">
              <Button className="btn-primary" data-testid="wishlist-browse-cta">Browse Products</Button>
            </Link>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map(function(item, idx) {
              var product = item.product;
              var hasSale = product.sale_price && product.sale_price < product.price;
              var displayPrice = hasSale ? product.sale_price : product.price;
              return (
                <motion.div
                  key={item.wishlist_id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: idx * 0.05 }}
                  className="card-tactical group"
                  data-testid={"wishlist-item-" + product.id}
                >
                  <Link to={"/shop/" + product.id} className="block">
                    <div className="relative aspect-square overflow-hidden bg-[#1c1c1c]">
                      {product.images && product.images.length > 0 ? (
                        <ProductImage src={product.images[0]} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-[#121212]"><Package className="w-16 h-16 text-[#3f3f46]" /></div>
                      )}
                      {!product.in_stock && (
                        <span className="absolute top-3 left-3 px-2 py-1 bg-red-500/90 text-white text-xs font-mono uppercase tracking-wider">Sold Out</span>
                      )}
                    </div>
                  </Link>
                  <div className="p-5">
                    <Link to={"/shop/" + product.id}>
                      <h3 className="font-heading font-bold text-white text-lg uppercase tracking-tight mb-2 line-clamp-2 group-hover:text-[#C0A062] transition-colors">{product.name}</h3>
                    </Link>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <span className="price-current">{formatPrice(displayPrice)}</span>
                        {hasSale && <span className="price-original">{formatPrice(product.price)}</span>}
                      </div>
                      {product.in_stock ? (
                        <span className="stock-badge in-stock"><span className="w-1.5 h-1.5 rounded-full bg-[#8CB369] mr-1.5"></span>In Stock</span>
                      ) : (
                        <span className="stock-badge out-of-stock"><AlertCircle className="w-3 h-3 mr-1" />Sold Out</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        className="btn-secondary flex-1"
                        onClick={function() { handleAddToCart(product); }}
                        disabled={!product.in_stock}
                        data-testid={"wishlist-add-cart-" + product.id}
                      >
                        <ShoppingCart className="w-4 h-4 mr-2" />
                        {product.in_stock ? "Add to Cart" : "Out of Stock"}
                      </Button>
                      <Button
                        variant="ghost"
                        className="text-[#A1A1AA] hover:text-red-400 hover:bg-red-500/10 px-3"
                        onClick={function() { handleRemove(product.id); }}
                        data-testid={"wishlist-remove-" + product.id}
                        aria-label="Remove from wishlist"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    {!product.in_stock && (
                      <p className="text-[10px] text-[#71717a] mt-2 font-mono uppercase">We'll email you when it's back in stock</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
